import { useMemo } from 'react'
import { Installments } from '../../../types/flyout'
import { DetailInfo, DetailRow } from '../DetailRow/DetailRow'
import { useTimeZone } from '../../utils/hooks/useTimeZone'
import { isNonEmptyNumber, numberFormat } from '@labrav/common'
import { get } from 'lodash'
import { useStyles } from './InstallmentsView.styles'
import { Divider } from '@mui/material'
import { useFlagValue } from '@labrav/flags'
import {
  dayFromIsoDate,
  monthsBetweenIsoDates,
} from '../PaymentScheduleV2/renewalSchedule'
import {
  formatInvoiceDate,
  getCalculatedTcv,
  resolveInvoiceDateIso,
  resolvePercent,
  shouldUseRenewalScheduleLayout,
  type InstallmentsViewLayout,
} from './InstallmentsView.utils'

type InstallmentsProps = {
  installments?: Installments[]
  currencySymbol?: string
  /** Figma renewal installments use % of TCV instead of amount */
  valueColumn?: 'amount' | 'percentage'
  hideTcv?: boolean
  /**
   * renewalSchedule: Payment timing / Day of the month / % of TCV
   * invoiceDate: Invoice date + amount|% (Figma view)
   * auto: invoice dates when paymentDate exists, else months-after table
   */
  layout?: InstallmentsViewLayout
  /** Used to turn monthsAfter + dayOfMonth into Invoice date */
  contractStartDate?: string
}

export const InstallmentsView = ({
  installments,
  currencySymbol,
  valueColumn = 'amount',
  hideTcv = false,
  layout = 'auto',
  contractStartDate,
}: InstallmentsProps) => {
  const { classes } = useStyles()
  const { timeZone } = useTimeZone()
  const { value: isRenewalPricingTermEnabled } =
    useFlagValue('renewalPricingTerm')
  const tcv = useMemo(() => {
    return getCalculatedTcv(installments || [])
  }, [installments])

  const showPercentage = valueColumn === 'percentage'
  const useRenewalSchedule = shouldUseRenewalScheduleLayout(
    layout,
    installments
  )

  if (useRenewalSchedule) {
    return (
      <div className={classes.root} data-testid={'installments-view'}>
        <DetailRow
          borderBottom
          children={
            <>
              <DetailInfo gridSize={4} varient="small" title="Payment timing" />
              <DetailInfo
                gridSize={4}
                varient="small"
                title="Day of the month"
              />
              <DetailInfo
                gridSize={4}
                varient="small"
                title="Percentage of Total Contract Value"
              />
            </>
          }
        />
        {(installments || []).map((installment, index) => {
          const percent = resolvePercent(installment, tcv)
          const monthsAfter = Number(
            installment.monthsAfter ??
              monthsBetweenIsoDates(
                contractStartDate || installments?.[0]?.paymentDate,
                installment.paymentDate,
                NaN
              )
          )
          const dayOfMonth =
            installment.dayOfMonth ?? dayFromIsoDate(installment.paymentDate)
          return (
            <DetailRow
              key={`renewal-inst-${index}`}
              borderBottom={index + 1 !== (installments || []).length}
              children={
                <>
                  <DetailInfo
                    varient="small"
                    value={
                      monthsAfter === 0
                        ? 'Contract start month'
                        : Number.isFinite(monthsAfter)
                          ? `${monthsAfter} ${monthsAfter === 1 ? 'month' : 'months'} after start`
                          : '-'
                    }
                    keyName="monthsAfter"
                    gridSize={4}
                  />
                  <DetailInfo
                    varient="small"
                    value={dayOfMonth != null ? String(dayOfMonth) : '-'}
                    keyName="dayOfMonth"
                    gridSize={4}
                  />
                  <DetailInfo
                    keyName="percentage"
                    varient="small"
                    value={percent != null ? `${percent}%` : '-'}
                    gridSize={4}
                  />
                </>
              }
            />
          )
        })}
      </div>
    )
  }

  const installmentList = (installments || []).map((installment, index) => {
    const dateValue = formatInvoiceDate(
      resolveInvoiceDateIso(installment, contractStartDate),
      timeZone
    )
    const amount = get(installment, 'amount') as number | null | undefined
    const percent = resolvePercent(installment, tcv)

    return (
      <DetailRow
        key={`invoice-inst-${index}`}
        borderBottom={index + 1 !== (installments || []).length}
        children={
          <>
            <DetailInfo
              varient="small"
              value={dateValue}
              keyName="dateValue"
              gridSize={6}
            />
            <DetailInfo
              keyName={showPercentage ? 'percentage' : 'amount'}
              varient="small"
              value={
                showPercentage ? (
                  percent != null ? (
                    `${percent}%`
                  ) : (
                    '-'
                  )
                ) : isNonEmptyNumber(amount as number) ? (
                  <div className={classes.amount}>
                    <span className={classes.currency}>{currencySymbol}</span>{' '}
                    {numberFormat(amount || 0, 2)}
                  </div>
                ) : (
                  '-'
                )
              }
              gridSize={6}
            />
          </>
        }
      />
    )
  })

  return (
    <div className={classes.root} data-testid={'installments-view'}>
      {!hideTcv && (
        <>
          <DetailRow
            children={
              <DetailInfo
                varient="small"
                title="Total contract value"
                keyName="tcv"
                value={`${currencySymbol}${numberFormat(tcv)}`}
              />
            }
          />
          <Divider style={{ margin: '8px 0' }} />
        </>
      )}
      <DetailRow
        borderBottom
        children={
          <>
            <DetailInfo gridSize={6} varient="small" title="Invoice date" />
            <DetailInfo
              gridSize={6}
              varient="small"
              titleClassName={
                isRenewalPricingTermEnabled ? classes.headerNowrap : undefined
              }
              title={
                showPercentage
                  ? 'Percentage of Total Contract Value'
                  : 'Installment amount'
              }
            />
          </>
        }
      />
      {installmentList}
    </div>
  )
}
